import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { Box, Button, Link, Typography } from '@mui/material';
import { auth } from '../../../api/firebase/config';
import { gray80, tertiaryButtonBackgroundColor, white } from '../../../constants/colors';
import { clearPatternListCache } from '../../../hooks/usePatternList';
import MetaTitle from '../../utils/MetaTitle';
import logoImage from '../../assets/logo_color.png';

const MESSAGE =
  'ご利用中のIPアドレスからはアクセスできません。\n貴社のセキュリティ設定（IPアドレス制限）により、このネットワークからの利用が制限されています。\n社内ネットワーク等の許可された環境から再度アクセスするか、貴社の管理者にご確認ください。';

const styles = {
  // メンテナンス画面（Maintenance.tsx）と同じ構成: 白背景・公式カラーロゴを中央配置
  container: {
    alignItems: 'center',
    backgroundColor: white,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minHeight: '100vh',
    px: '24px',
    py: '48px',
    width: '100%',
  },
  content: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '560px',
    textAlign: 'center',
    width: '100%',
  },
  logo: {
    height: 'auto',
    width: '300px',
    '@media screen and (max-width:600px)': {
      width: '240px',
    },
  },
  message: {
    color: gray80,
    fontSize: '18px',
    fontWeight: '400',
    lineHeight: 1.8,
    mt: '40px',
    whiteSpace: 'pre-wrap',
    '@media screen and (max-width:600px)': {
      fontSize: '16px',
      mt: '32px',
    },
  },
  contact: {
    color: gray80,
    fontSize: '16px',
    mt: '32px',
    '@media screen and (max-width:600px)': {
      fontSize: '14px',
      mt: '24px',
    },
  },
  logoutButton: {
    height: '44px',
    minWidth: '160px',
    mt: '32px',
    '@media screen and (max-width:600px)': {
      mt: '24px',
    },
  },
};

/**
 * IP アドレス制限による 403 画面（#2982）。
 * テナントの許可リスト外 IP からアクセスした場合に表示する。お問合せ導線あり（PRD §4-1）。
 * 別アカウント（管理者等）への切替ができるよう、ログアウト導線も設ける。
 */
const IpRestricted = (): JSX.Element => {
  const navigate = useNavigate();

  const handleLogout = async (): Promise<void> => {
    try {
      // パターンリストのキャッシュをクリア（GlobalHeader のログアウトと同じ手順）
      clearPatternListCache();
      await signOut(auth);
      navigate('/');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[IpRestricted] ログアウトエラー:', error);
    }
  };

  return (
    <>
      <MetaTitle customTitle="アクセスが制限されています" />
      <Box sx={styles.container}>
        <Box sx={styles.content}>
          <Box component="img" src={logoImage} alt="Gen Drive" sx={styles.logo} />
          <Typography component="h1" sx={styles.message}>
            {MESSAGE}
          </Typography>
          <Typography component="p" sx={styles.contact}>
            {/* href だとページ全体が再読み込みされ、アプリの初期化をやり直すことになる。
                SPA 内遷移にして初期化済みの状態のままお問い合わせ画面へ移す（#3104） */}
            解決しない場合は
            <Link component={RouterLink} to="/contact">
              お問い合わせ
            </Link>
            ください。
          </Typography>
          <Button onClick={handleLogout} sx={[tertiaryButtonBackgroundColor, styles.logoutButton]}>
            ログアウト
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default IpRestricted;
